import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Play, Disc3, AlertCircle, ExternalLink } from 'lucide-react';

// Platform icons
const SpotifyIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

const AppleMusicIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.401-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.295-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.785-.364-2.172-1.158-.39-.804-.245-1.784.503-2.39.424-.344.928-.517 1.466-.612.344-.06.692-.09 1.038-.142.2-.03.4-.07.595-.12.089-.023.167-.067.167-.183V9.967c0-.12-.04-.2-.161-.212-.038-.004-.077-.01-.115-.01-.455-.036-.91-.07-1.364-.106l-4.31-.34c-.075-.005-.15-.012-.226-.012-.09 0-.13.048-.132.138v7.63c.002.224-.022.447-.075.666-.16.664-.541 1.158-1.153 1.45a2.91 2.91 0 01-.826.253c-.862.15-1.614-.025-2.234-.632-.678-.664-.763-1.588-.378-2.343.323-.635.86-1.01 1.507-1.208.44-.135.893-.19 1.35-.248.263-.033.528-.055.79-.09.163-.023.32-.066.38-.253.023-.074.035-.153.035-.23V6.324c0-.22.063-.33.27-.392.098-.03.2-.048.302-.06l4.232-.51 3.537-.426c.08-.01.16-.018.24-.03.107-.016.17.02.196.126.012.048.017.1.017.15v4.932z"/>
  </svg>
);

export const Drops = ({ albums, onPlayAlbum, currentlyPlayingAlbum, isPlaying }) => {
  const [activeAlbum, setActiveAlbum] = useState(albums?.[0] || null);
  const [hoveredAlbum, setHoveredAlbum] = useState(null);

  if (!albums || albums.length === 0) return null;

  return (
    <section id="drops" className="relative py-24 sm:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-background" />
      
      {/* Dynamic background based on active album */}
      <AnimatePresence mode="wait">
        {activeAlbum && (
          <motion.div
            key={activeAlbum.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0"
          >
            <div
              className="absolute inset-0 bg-cover bg-center opacity-10"
              style={{ backgroundImage: `url('${activeAlbum.coverArtUrl}')` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
            <div className={`absolute inset-0 bg-gradient-to-r ${
              activeAlbum.era === 'FUOCO' ? 'from-crimson/10' : 'from-neonBlue/10'
            } to-transparent`} />
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="absolute inset-0 grain" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 sm:mb-20"
        >
          <span className="text-[10px] font-body uppercase tracking-[0.4em] text-crimson mb-4 block">
            Latest Releases
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl text-ivory">
            THE DROPS
          </h2>
          <p className="mt-4 sm:mt-6 text-sm sm:text-base text-ivory/50 max-w-lg mx-auto font-body tracking-wide">
            Each release is a world. Each track, a doorway.
          </p>
        </motion.div>

        {/* Albums Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {albums.map((album, index) => {
            const hasPreview = album.tracks?.some(t => t.previewUrl);
            const isCurrentlyPlaying = currentlyPlayingAlbum?.id === album.id && isPlaying;
            const links = album.streamingLinks || {};
            
            return (
              <motion.div
                key={album.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                onMouseEnter={() => {
                  setHoveredAlbum(album.id);
                  setActiveAlbum(album);
                }}
                onMouseLeave={() => setHoveredAlbum(null)}
                onClick={() => hasPreview && onPlayAlbum(album)}
                className="group cursor-pointer"
              >
                <div
                  className={`relative overflow-hidden border transition-all duration-600 ${
                    activeAlbum?.id === album.id
                      ? 'border-crimson/50 shadow-crimson'
                      : 'border-border/30 hover:border-crimson/30'
                  }`}
                >
                  {/* Cover Art */}
                  <div className="aspect-square overflow-hidden relative">
                    <motion.img
                      src={album.coverArtUrl}
                      alt={album.title}
                      className="w-full h-full object-cover"
                      animate={{
                        scale: hoveredAlbum === album.id ? 1.08 : 1,
                      }}
                      transition={{ duration: 0.8 }}
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
                    
                    {/* Play overlay */}
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center bg-crimson/20 backdrop-blur-sm"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: hoveredAlbum === album.id ? 1 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {hasPreview ? (
                        <Button
                          variant="play"
                          size="iconXl"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPlayAlbum(album);
                          }}
                          className="shadow-crimson"
                        >
                          <Play className="w-8 h-8 ml-1" />
                        </Button>
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-ivory/70">
                          <AlertCircle className="w-8 h-8" />
                          <span className="text-xs uppercase tracking-wider">Preview Coming Soon</span>
                        </div>
                      )}
                    </motion.div>

                    {/* Playing indicator */}
                    {isCurrentlyPlaying && (
                      <div className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1 bg-crimson text-primary-foreground text-xs font-body uppercase tracking-wider">
                        <span className="w-2 h-2 bg-primary-foreground rounded-full animate-pulse" />
                        Playing
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4 sm:p-6">
                    <div className="flex items-center gap-2 mb-2 sm:mb-3">
                      <span className="text-[10px] font-body uppercase tracking-[0.3em] text-crimson">
                        {new Date(album.releaseDate).getFullYear()}
                      </span>
                      <span className="w-4 h-px bg-crimson/30" />
                      <span className={`text-[10px] font-body uppercase tracking-[0.2em] ${
                        album.era === 'FUOCO' ? 'text-crimson' : 'text-neonBlue'
                      }`}>
                        {album.era} ERA
                      </span>
                    </div>
                    
                    <h3 className="font-display text-xl sm:text-2xl text-ivory mb-2">
                      {album.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-ivory/50 font-body mb-4 line-clamp-2">
                      {album.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {album.trackCount} Tracks
                      </span>
                      
                      {/* Multiple Streaming Links */}
                      <div className="flex items-center gap-1">
                        {links.spotify && (
                          <a
                            href={links.spotify}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-[#1DB954] transition-colors"
                            title="Spotify"
                          >
                            <SpotifyIcon />
                          </a>
                        )}
                        {links.appleMusic && (
                          <a
                            href={links.appleMusic}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-[#FA243C] transition-colors"
                            title="Apple Music"
                          >
                            <AppleMusicIcon />
                          </a>
                        )}
                        {links.hyperfollow && (
                          <a
                            href={links.hyperfollow}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-crimson transition-colors"
                            title="All Platforms"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Rotating disc icon */}
                  <motion.div
                    className="absolute top-4 left-4"
                    animate={{ rotate: hoveredAlbum === album.id ? 360 : 0 }}
                    transition={{ duration: 4, repeat: hoveredAlbum === album.id ? Infinity : 0, ease: 'linear' }}
                  >
                    <Disc3 className="w-5 h-5 sm:w-6 sm:h-6 text-crimson/30" />
                  </motion.div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
